import { useState } from 'react';
import useGlobalFilters from '../../../../hooks/useGlobalFilters';
import useTableMenu from '../../../../hooks/useTableMenu';
import {
    useDownloadVisitaActaMutation,
    useGetTiposVisitasQuery
} from '../../../../services/visitasApi';
import { SRT_STATES } from '../../consts/states';
import { TEST_IDS } from '../../consts/testIds';
import { useTranslation } from 'react-i18next';
import useTranslate from '../../../../hooks/useTranslate';
import { useGenerateVisitFilesSRTMutation, useGetVisitsSRTQuery } from '../../../../services/rgrlVinculationApi';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../../hooks/reduxHooks';
import { showAlert } from '../../../../features/slices/applicationSlice';
import { enqueueSnackbar } from 'notistack';
import { downloadBlobAsFile } from '../../../Visitas/utils/common';
import useDownloadFile from '../../../../hooks/useDownloadFile';
import { useLazyGetPresentacionRGRLPDFbyIdQuery } from '../../../../services/presentacionesApi';
import { getCurrentDate, getDateTextFieldFormat } from '../../../../utils/common';
import { useRoles } from '../../../../hooks/useRoles';
import { useOrganizationFilter } from '../../../../contexts/OrganizationFilterContext';

const useFileGenerationVisits = (handleCloseDrawer: any) => {
    const { t } = useTranslation('filesGenerationSRT');
    
    const { t: visitsDownloadTranslations } = useTranslation('visitas');

    const visitsFilters = useGlobalFilters();
    const { filters, extraFilters } = visitsFilters;

    const { isDownloading, downloadFile } = useDownloadFile();
    const { translate } = useTranslate(t);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { data: visitTypes, isFetching: isFetchingVisitTypes } = useGetTiposVisitasQuery(null);
    const [downloadVisitaActa, { isLoading: isDownloadingVisitActa }] =
        useDownloadVisitaActaMutation();
    const [getPresentacionRGRLPDFbyId] = useLazyGetPresentacionRGRLPDFbyIdQuery();
    const [generateVisitsFiles, { isLoading: isGeneratingVisitsFiles }] =
        useGenerateVisitFilesSRTMutation();

    const [selectedVisits, setSelectedVisits] = useState<any[]>([]);

    const [selected, setSelected] = useState<any | null>(null);
    const [selectedItemFromAction, setSelectedItemFromAction] = useState<any>(null);

    const visitsTableMenu = useTableMenu();
    const { isPerfilBackOffice } = useRoles();
    const { selectedOrganization } = useOrganizationFilter();

    const { handleOpenCard, anchorToEventElement, openMenu, closeMenu } = visitsTableMenu;

    const handleCloseMenu = () => {
        closeMenu();
    };

    const handleItemCheck = (selectedVisit: any) => {
        if (isPerfilBackOffice && (!selectedOrganization || !selectedOrganization?.id)) { 
            
            enqueueSnackbar(translate('drawers.generateFilesSRT.enqueues.warning.noOrganizationSelected'), {
                variant: 'warning'
            });
            return;
        }

        if (selectedVisit?.estadoSRT === SRT_STATES.ERROR) return;

        const updatedSectors = selectedVisits.some((visit) => visit.id === selectedVisit.id)
            ? selectedVisits.filter((visit) => visit.id !== selectedVisit.id)
            : [...selectedVisits, selectedVisit];

        setSelectedVisits(updatedSectors);
    };

    const handleRowClick = (selectedRowItem: any) => {
        setSelectedItemFromAction(selectedRowItem);
        setSelected(selectedRowItem);
        handleOpenCard();
    };

    const handleClickAction = (event: any, item: any, index: any) => {
        setSelectedItemFromAction(item);
        anchorToEventElement(event);
        openMenu();
    };

    const {
        data: visitas,
        refetch: refetchVisitas,
        isFetching: isFechingVisits
    } = useGetVisitsSRTQuery({ ...filters, ...(extraFilters?.idOrganizacion ? { idOrganizacion: extraFilters.idOrganizacion } : {}) });

    const visitTypesOptions =
        visitTypes?.data?.tipos?.map((tipo: any) => ({
            id: tipo.id,
            nombre: tipo.descripcion,
            value: tipo.id
        })) || [];

    const handleDownload = async ({
        tipo,
        requestFn,
        requestParams,
        filename,
        alertKeys
    }: {
        tipo: 'binary' | 'url';
        requestFn: any;
        requestParams: any;
        filename: string;
        alertKeys: {
            generating: string;
            success?: string;
            error: string;
            notFoundId?: string;
        };
    }) => {
        if (!selectedItemFromAction) return;

        if (tipo === 'url' && !selectedItemFromAction?.id) {
            dispatch(showAlert({ icon: 'error', message: t(alertKeys.notFoundId!) }));
            return;
        }

        try {
            dispatch(showAlert({ icon: 'info', message: t(alertKeys.generating), loading: true }));

            const response = await requestFn(requestParams).unwrap();

            if (tipo === 'binary' && response?.data) {
                downloadBlobAsFile(response.data, filename);
            } else if (tipo === 'url' && response?.data?.presignedUrls?.length > 0) {
                const url = response.data.presignedUrls[0];
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${filename}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }

            dispatch({ type: 'application/hideAlert' });
            if (alertKeys.success) enqueueSnackbar(t(alertKeys.success), { variant: 'success' });
        } catch (error: any) {
            dispatch({ type: 'application/hideAlert' });

            let errorMessage = t(alertKeys.error);

            try {
                if (error?.data instanceof Blob) {
                    const text = await error.data.text();
                    const json = JSON.parse(text);
                    errorMessage = json?.message || errorMessage;
                } else if (error?.data?.message) {
                    errorMessage = error.data.message;
                } else if (error?.error?.data?.message) {
                    errorMessage = error.error.data.message;
                } else if (error?.error) {
                    errorMessage = error.error;
                }
            } catch {
                
            }

            dispatch(showAlert({ icon: 'error', message: errorMessage }));
        }
    };

    const downloadVerosimilitud = () =>
        handleDownload({
            tipo: 'binary',
            requestFn: downloadVisitaActa,
            requestParams: {
                idVisita: selectedItemFromAction?.id,
                idTipoVisita: selectedItemFromAction?.tipo,
                tipoActa: 2
            },
            filename: 'ActaVerosimilitud',
            alertKeys: {
                generating: visitsDownloadTranslations('alerts.downloadVerosimilitud.generating'),
                error: visitsDownloadTranslations('alerts.downloadVerosimilitud.error')
            }
        });

    const downloadActa = () =>
        handleDownload({
            tipo: 'binary',
            requestFn: downloadVisitaActa,
            requestParams: {
                idVisita: selectedItemFromAction?.id,
                idTipoVisita: selectedItemFromAction?.tipo,
                tipoActa: 1
            },
            filename: visitsDownloadTranslations('table.actions.downloadActaFilename'),
            alertKeys: {
                generating: visitsDownloadTranslations('alerts.downloadActa.generating'),
                success: visitsDownloadTranslations('alerts.downloadActa.success') as string,
                error: visitsDownloadTranslations('alerts.downloadActa.error')
            }
        });

    const downloadRgrl = () =>
        handleDownload({
            tipo: 'url',
            requestFn: getPresentacionRGRLPDFbyId,
            requestParams: {
                idPresentacion: selectedItemFromAction?.id,
                typeId: 2
            },
            filename: 'PresentacionRGRL',
            alertKeys: {
                generating: visitsDownloadTranslations('alerts.downloadRgrl.generating'),
                error: visitsDownloadTranslations('alerts.downloadRgrl.error'),
                notFoundId: visitsDownloadTranslations('alerts.downloadRgrl.notFoundId') as string
            }
        });

    const generateVisitsTableMenuItems = (item: any) => {
        return [
            {
                testID: TEST_IDS.table.visits.actions.seeVisit,
                onClick: () => {
                    handleCloseMenu();
                    navigate(`/backoffice/visitas/ejecucion/${item?.id}`);
                },
                text: translate('visits.table.actions.seeVisit')
            },
            // puede que se necesite a futuro.
            // {
            //     testID: TEST_IDS.table.visits.actions.editVisit,
            //     onClick: () => {
            //         if (item?.estadoSRT !== SRT_STATES.ERROR) return;

            //         handleCloseMenu();
            //         navigate(`/backoffice/visitas/editar/${item?.id}`);
            //     },
            //     text: translate('visits.table.actions.editVisit')
            // },
            {
                testID: TEST_IDS.table.visits.actions.downloadVisitReport,
                onClick: () => {
                    // "visitTypes":
                    // "456": "Genérica",
                    // "457": "Verosimilitud",
                    // "458": "Completar RGRL"
                    if (item?.tipo === 456) {
                        downloadActa();
                    }
                    if (item?.tipo === 457) {
                        downloadVerosimilitud();
                    }
                    if (item?.tipo === 458) {
                        downloadRgrl();
                    }
                },
                text: translate('visits.table.actions.downloadVisitReport')
            }
        ];
    };

    const showGenerationLimitAlert = () => {
        dispatch(
            showAlert({
                title: translate('alerts.generationLimit.title'),
                message: translate('alerts.generationLimit.message'),
                icon: 'error',
                confirmText: translate('buttons.accept')
            })
        );
    };

    const generateAndDownloadFiles = async () => {
        try {
            if (selectedVisits?.length <= 0) {
                enqueueSnackbar(
                    translate('drawers.generateFilesSRT.enqueues.warning.noPresentationSelected'),
                    {
                        variant: 'warning'
                    }
                );
                return;
            }
            const visitsIds = selectedVisits.map((visit) => visit?.id);
            const adaptedBody = {
                idsVisita: visitsIds
            };
            const response: any = await generateVisitsFiles(adaptedBody);
            if (
                response?.error?.data?.statusCode === 400 &&
                response?.error?.data?.message ===
                    'Ha superado el límite de generación de archivos diarios.'
            ) {
                showGenerationLimitAlert();
                return;
            }
            if (
                response?.error?.data?.statusCode === 500 &&
                response?.error?.data?.message === 'Error al generar archivos.'
            ) {
                enqueueSnackbar(
                    translate('drawers.generateFilesSRT.enqueues.error.somethingWentWrong'),
                    {
                        variant: 'error'
                    }
                );
                return;
            }

            if (response.data.data.presignedUrl) {
                const todayDate = getCurrentDate();
                const formatDate = getDateTextFieldFormat(todayDate);
                const fileName = `${t('drawers.generateFilesSRT.filename')}_${formatDate}`;
                await downloadFile(response.data.data.presignedUrl, '.zip', fileName);
            }

            enqueueSnackbar(translate('drawers.generateFilesSRT.enqueues.success.generated'), {
                variant: 'success'
            });
            setSelectedVisits([]);
            handleCloseDrawer();
        } catch (error) {
            enqueueSnackbar(
                translate('drawers.generateFilesSRT.enqueues.error.somethingWentWrong'),
                {
                    variant: 'error'
                }
            );
        }
    };

    const visitsTableMenuItems = generateVisitsTableMenuItems(selectedItemFromAction);

    return {
        visitsFilters,
        visitas,
        isFechingVisits,
        visitTypes,
        isFetchingVisitTypes,
        visitTypesOptions,
        visitsTableMenu,
        selectedVisits,
        selectedItemFromAction,
        selected,
        visitsTableMenuItems,
        isDownloading,
        isDownloadingVisitActa,
        isGeneratingVisitsFiles,
        generateAndDownloadFiles,
        handleRowClick,
        handleClickAction,
        handleItemCheck
    };
};

export default useFileGenerationVisits;
